import {
  FieldSwitch,
  Page,
  SimplePagination,
  Table,
  Tag,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { ChangeEvent } from 'react';

import { integrationsApi } from 'apis/integrations';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import { IdentityRecordsTable } from './components/identityRecordsTable';

export const SettingsEmailIdentitiesTableView = (): JSX.Element => {
  const {
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    items: identities,
    asyncStatus,
  } = usePaginatedEndpoint({
    endpoint: integrationsApi.endpoints.readEmailIdentities,
    initialArg: {
      limit: 25,
      offset: 0,
    },
    itemsSelector: ({ data }) => data.email_identities,
  });
  const isLoading = asyncStatus === 'pending';

  const [updatePixelTracking] =
    integrationsApi.useUpdatePixelTrackingStatusMutation();

  const handlePixelTracking = async (enabled: boolean, identityId: string) => {
    await updatePixelTracking({ identityId, enabled });
  };

  return (
    <Page.Section title="Email identities">
      <Table
        expandableRowsProps={{
          expandableRows: true,
        }}
        loadingStrategy="spinner"
        loading={isLoading}
        emptyState={
          !isLoading && identities.length === 0
            ? {
                title: 'Nothing to show',
                description: 'No email identities found',
              }
            : undefined
        }
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th width="55%">Identity</Table.Th>
            <Table.Th width="30%">Status</Table.Th>
            <Table.Th width="15%">Tracking pixel</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {identities.map((emailIdentity, index) => (
            <Table.Tr
              key={emailIdentity.organization_id}
              rowId={`row-${index}`}
              expandableContent={
                <IdentityRecordsTable emailIdentity={emailIdentity} />
              }
            >
              <Table.Td>{emailIdentity.identity}</Table.Td>
              <Table.Td>
                <Tag
                  noBorder
                  color={
                    emailIdentity.is_verified
                      ? colors.green[500]
                      : colors.red[500]
                  }
                />
              </Table.Td>
              <Table.Td>
                <FieldSwitch
                  input={{
                    name: `switch-${index}`,
                    checked: emailIdentity.delivery_tracking?.tracking_enabled,
                    value: 'false',
                    disabled:
                      !emailIdentity.delivery_tracking
                        ?.cname_dns_record_published,
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      handlePixelTracking(
                        event.target.checked,
                        emailIdentity.organization_id,
                      ),
                  }}
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>

        <Table.Tfoot>
          <Table.Tr>
            <Table.Td colSpan={3}>
              <SimplePagination
                nextButtonClick={nextPage}
                nextButtonDisabled={!hasNextPage}
                previousButtonClick={previousPage}
                previousButtonDisabled={!hasPreviousPage}
                loading={isLoading}
              />
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Page.Section>
  );
};
